<template>
    <div class="table-active-tabs" v-if="hasActiveTabs" @click="closeContextMenu" :key="activeTabId">
        <div class="tabs-container">
            <!-- Home Tab -->
            <a-tooltip :title="table.name" placement="bottom">
                <div
                    class="tab home-tab"
                    :class="{ active: activeTabId === 'home' }"
                    @click="selectTab('home')"
                >
                    <i class="material-icons">home</i>
                    <span>{{ getHomeTitleShort(table.name) }}</span>
                </div>
            </a-tooltip>

            <!-- Record Tabs -->
            <a-tooltip
                v-for="tab in activeTabs"
                :key="`tooltip-${tab.recordId}`"
                :title="getFullTabTitle(tab)"
                placement="bottom"
            >
                <div
                    :key="`tab-${tab.recordId}`"
                    class="tab record-tab"
                    :class="{ active: activeTabId === tab.recordId }"
                    @click="selectTab(tab.recordId)"
                    @contextmenu.prevent="onTabRightClick($event, tab)"
                >
                    <span class="tab-title">{{ getTabTitle(tab) }}</span>
                    <a-tooltip title="Sekmeyi Kapat" placement="bottom">
                        <i class="material-icons close-btn" @click.stop="closeTab(tab.recordId)">close</i>
                    </a-tooltip>
                </div>
            </a-tooltip>
        </div>

        <!-- Context Menu -->
        <div
            v-if="contextMenuVisible"
            class="context-menu"
            :style="{
                left: `${contextMenuX}px`,
                top: `${contextMenuY}px`,
            }"
            @click.stop
        >
            <div class="context-menu-item" @click="closeTab(contextMenuTab.recordId)">
                <i class="material-icons">close</i>
                <span>Kapat</span>
            </div>
            <div class="context-menu-item" @click="closeOtherTabs(contextMenuTab.recordId)">
                <i class="material-icons">close_fullscreen</i>
                <span>Diğer Sekmeleri Kapat</span>
            </div>
            <div
                class="context-menu-item"
                :class="{ disabled: !canCloseTabsToRight(contextMenuTab.recordId) }"
                @click="closeTabsToRight(contextMenuTab.recordId)"
            >
                <i class="material-icons">keyboard_arrow_right</i>
                <span>Sağdaki Sekmeleri Kapat</span>
            </div>
            <div
                class="context-menu-item"
                :class="{ disabled: !canCloseTabsToLeft(contextMenuTab.recordId) }"
                @click="closeTabsToLeft(contextMenuTab.recordId)"
            >
                <i class="material-icons">keyboard_arrow_left</i>
                <span>Soldaki Sekmeleri Kapat</span>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import { mapGetters, mapActions } from "vuex";

export default Vue.extend({
    name: "TableActiveTabs",
    props: {
        table: {
            type: Object,
            required: true,
        },
        activeTabId: {
            type: String,
            default: "home",
        },
    },
    data() {
        return {
            contextMenuVisible: false,
            contextMenuX: 0,
            contextMenuY: 0,
            contextMenuTab: null,
        };
    },
    computed: {
        ...mapGetters("table", ["getActiveTabsByTableId", "getActiveTabsCount"]),
        activeTabs() {
            if (!this.table || !this.table._id) return [];
            return this.getActiveTabsByTableId(this.table._id);
        },
        hasActiveTabs() {
            if (!this.table || !this.table._id) return false;
            return this.getActiveTabsCount(this.table._id) > 0;
        },
    },
    methods: {
        ...mapActions("table", ["removeActiveTab", "closeOtherTabs", "closeTabsToRight", "closeTabsToLeft"]),
        selectTab(tabId) {
            this.$emit("tab-selected", tabId);
        },
        closeTab(recordId) {
            this.removeActiveTab({
                tableId: this.table._id,
                recordId,
            });

            // Eğer kapatılan sekme aktif sekmeyse, home'a dön
            if (this.activeTabId === recordId) {
                this.selectTab("home");
            }

            this.closeContextMenu();
        },
        getTabTitle(tab) {
            console.log("getTabTitle", tab);
            // Sekme başlığını kısalt
            const maxLength = 12;
            if (tab.title && tab.title.length > maxLength) {
                return tab.title.substring(0, maxLength) + "...";
            }
            return tab.title || `Kayıt ${tab.recordId.substring(0, 6)}`;
        },
        onTabRightClick(event, tab) {
            this.contextMenuVisible = true;
            this.contextMenuX = event.pageX;
            this.contextMenuY = event.pageY;
            this.contextMenuTab = tab;
        },
        closeContextMenu() {
            this.contextMenuVisible = false;
            this.contextMenuTab = null;
        },
        closeOtherTabs(keepRecordId) {
            this.$store.dispatch("table/closeOtherTabs", {
                tableId: this.table._id,
                keepRecordId,
            });

            // Eğer aktif sekme kapanmışsa, kalan sekmeyi aktif yap
            if (this.activeTabId !== keepRecordId && this.activeTabId !== 'home') {
                this.selectTab(keepRecordId);
            }

            this.closeContextMenu();
        },
        closeTabsToRight(fromRecordId) {
            if (!this.canCloseTabsToRight(fromRecordId)) return;

            this.$store.dispatch("table/closeTabsToRight", {
                tableId: this.table._id,
                fromRecordId,
            });

            // Eğer aktif sekme kapanmışsa, kalan en sağdaki sekmeyi aktif yap
            const remainingTabs = this.activeTabs.slice(0, this.getTabIndex(fromRecordId) + 1);
            if (!remainingTabs.find(tab => tab.recordId === this.activeTabId)) {
                this.selectTab(fromRecordId);
            }

            this.closeContextMenu();
        },
        closeTabsToLeft(fromRecordId) {
            if (!this.canCloseTabsToLeft(fromRecordId)) return;

            this.$store.dispatch("table/closeTabsToLeft", {
                tableId: this.table._id,
                fromRecordId,
            });

            // Eğer aktif sekme kapanmışsa, kalan en soldaki sekmeyi aktif yap
            const remainingTabs = this.activeTabs.slice(this.getTabIndex(fromRecordId));
            if (!remainingTabs.find(tab => tab.recordId === this.activeTabId)) {
                this.selectTab(fromRecordId);
            }

            this.closeContextMenu();
        },
        getTabIndex(recordId) {
            return this.activeTabs.findIndex(tab => tab.recordId === recordId);
        },
        canCloseTabsToRight(recordId) {
            const index = this.getTabIndex(recordId);
            return index !== -1 && index < this.activeTabs.length - 1;
        },
        canCloseTabsToLeft(recordId) {
            const index = this.getTabIndex(recordId);
            return index !== -1 && index > 0;
        },
        getHomeTitleShort(name) {
            const maxLength = 12;
            if (name && name.length > maxLength) {
                return name.substring(0, maxLength) + "...";
            }
            return name;
        },
        getFullTabTitle(tab) {
            if (!tab) return "Bilinmeyen Sekme";

            if (tab.title && tab.title.trim()) {
                return tab.title;
            }

            if (tab.recordId) {
                return `Kayıt ${tab.recordId.substring(0, 8)}`;
            }

            return "Bilinmeyen Kayıt";
        },
    },
    mounted() {
        document.addEventListener("click", this.closeContextMenu);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.closeContextMenu);
    },
});
</script>

<style scoped lang="scss">
.table-active-tabs {
    background-color: #f5f5f5;
    border-bottom: 1px solid #e8e8e8;
    padding: 0;
    overflow-x: auto;
    overflow-y: hidden;
    position: relative;

    &::-webkit-scrollbar {
        height: 3px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: #ccc;
        border-radius: 2px;
    }
}

.tabs-container {
    display: flex;
    align-items: center;
    gap: 0;
    min-height: 32px;
    white-space: nowrap;
    padding-left: 0;
}

.tab {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 6px 10px;
    background-color: #e6e6e6;
    border: 1px solid #d0d0d0;
    border-bottom: none;
    cursor: pointer;
    font-size: 12px;
    color: #666;
    transition: all 0.15s ease;
    max-width: 120px;
    min-width: 70px;
    position: relative;
    border-radius: 0;
    border-top:none !important;

    &:first-child {
        padding-left: 8px;
        border-radius: 6px 6px 0 0;
        border-left: 1px solid #d0d0d0;
    }

    &:not(:first-child) {
        margin-left: -1px;
        border-left: none;
    }

    &:last-child {
        border-radius: 0 6px 0 0;
        border-right: 1px solid #d0d0d0;
    }

    &:hover {
        background-color: #ddd;
        z-index: 2;
    }

    &.active {
        background-color: white;
        color: #333;
        border-color: #c0c0c0;
        z-index: 3;
        margin-bottom: -1px;
        border-bottom: 1px solid white;
    }

    .material-icons {
        font-size: 14px;
        flex-shrink: 0;
    }

    .tab-title {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        min-width: 0;
    }

    .close-btn {
        font-size: 12px;
        color: #999;
        padding: 1px;
        border-radius: 2px;
        transition: all 0.15s ease;
        flex-shrink: 0;

        &:hover {
            background-color: rgba(0, 0, 0, 0.1);
            color: #666;
        }
    }

    &.home-tab {
        font-weight: 500;
        border-left: none !important;

        .material-icons {
            color: #1890ff;
        }

        &.active {
            .material-icons {
                color: #1890ff;
            }
        }
    }
}

// Özel sekmeler arasındaki bağlantı çizgileri
.tab:not(.active) + .tab:not(.active) {
    &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 20%;
        bottom: 20%;
        width: 1px;
        background-color: rgba(0, 0, 0, 0.1);
    }
}

.context-menu {
    position: fixed;
    z-index: 9999;
    background: white;
    border: 1px solid #ddd;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    min-width: 200px;
    padding: 4px 0;

    .context-menu-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        cursor: pointer;
        font-size: 13px;
        color: #333;
        transition: background-color 0.15s ease;

        &:hover:not(.disabled) {
            background-color: #f5f5f5;
        }

        &.disabled {
            color: #ccc;
            cursor: not-allowed;

            .material-icons {
                color: #ccc;
            }
        }

        .material-icons {
            font-size: 16px;
            color: #666;
        }
    }
}
</style>
